import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertTriangle, ExternalLink } from 'lucide-react';
import { api } from '../../utils/api';
import { useLanguage } from '../../context/LanguageContext';

/**
 * TREATX-LEGAL-SSOT-CANON-01 Phase 2 — GENERIC legal-requirements renderer.
 *
 * The frontend knows ONLY the journey key. Which documents are required,
 * their versions, locales, presentation mechanism and blocking status are
 * resolved server-side from the canonical `legal_policy_bindings` authority
 * via GET /api/legal/journey/{journeyKey}/requirements. There are NO
 * hard-coded document keys here — adding/removing a document in Agreement
 * Management changes this UI with no code change.
 *
 * Presentation copy derives from each document's canonical acceptance
 * mechanism (i18n templated), never a single universal sentence.
 */
export const LegalRequirements = ({ journeyKey, country, role = 'patient', onChange }) => {
  const { language } = useLanguage();
  const { t } = useTranslation();
  const [state, setState] = useState({ loading: true, error: null, items: [] });
  const [accepted, setAccepted] = useState({});

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      // `role` is an explicit prop (default 'patient' — existing callers
      // keep their behaviour unchanged). Provider-side journeys pass their
      // canonical RBAC role.
      const params = new URLSearchParams({ locale: language, role });
      if (country) params.append('country', country);
      const { data } = await api.get(`/legal/journey/${journeyKey}/requirements?${params.toString()}`);
      setState({ loading: false, error: null, items: data.requirements || [] });
    } catch (e) {
      setState({ loading: false, error: (e?.response?.data?.message || e.message || 'load_failed'), items: [] });
    }
  }, [journeyKey, country, language, role]);

  useEffect(() => { load(); }, [load]);

  // Emit the accepted payload + whether all blocking docs are satisfied.
  useEffect(() => {
    const items = state.items || [];
    const payload = items
      .filter((i) => accepted[i.content_key])
      .map((i) => ({
        content_key: i.content_key,
        content_version: i.version,
        locale: i.locale,
        accepted: true,
        acceptance_mechanism: i.acceptance_mechanism,
      }));
    const blockingOk = items.filter((i) => i.blocking).every((i) => accepted[i.content_key]);
    onChange && onChange({ payload, blockingOk, ready: !state.loading && !state.error, count: items.length });
  }, [accepted, state.items, state.loading, state.error]); // eslint-disable-line react-hooks/exhaustive-deps

  const mechLabel = (mech) => {
    const key = `legal.mechanism.${mech || 'acknowledgement'}`;
    const fallback = t('legal.mechanism.acknowledgement');
    const v = t(key);
    return v === key ? fallback : v;
  };

  if (state.loading) {
    return (
      <div className="text-xs text-slate-500 py-3" data-testid="legal-requirements-loading">
        <Loader2 className="h-3 w-3 animate-spin inline mr-2" /> {t('legal.loading')}
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="text-xs py-3" data-testid="legal-requirements-error">
        <span className="text-rose-600 inline-flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> {t('legal.loadError')}
        </span>
        <button type="button" onClick={load} className="ml-2 underline text-slate-600" data-testid="legal-requirements-retry">
          {t('legal.retry')}
        </button>
      </div>
    );
  }
  if (!state.items.length) return null;

  return (
    <div className="space-y-2" data-testid="legal-requirements">
      {state.items.map((i) => (
        <label
          key={i.content_key}
          className="flex items-start gap-2 text-xs text-foreground/80"
          data-testid={`legal-req-${i.content_key}`}
        >
          <input
            type="checkbox"
            className="mt-1"
            checked={!!accepted[i.content_key]}
            onChange={() => setAccepted((a) => ({ ...a, [i.content_key]: !a[i.content_key] }))}
            data-testid={`legal-req-checkbox-${i.content_key}`}
          />
          <span>
            {mechLabel(i.acceptance_mechanism)}{' '}
            {i.public_route ? (
              <a
                href={`/#${i.public_route}`}
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-foreground inline-flex items-center gap-0.5"
                data-testid={`legal-req-link-${i.content_key}`}
              >
                {i.title} <ExternalLink className="h-3 w-3" />
              </a>
            ) : (
              <span className="font-medium">{i.title}</span>
            )}
            {i.is_locale_fallback && (
              <span className="ml-1 text-[10px] text-slate-400">({t('legal.translatedFromEnglish')})</span>
            )}
            {i.blocking && <span className="text-red-500"> *</span>}
          </span>
        </label>
      ))}
    </div>
  );
};

export default LegalRequirements;
